import "./Tables.css"
import "./DataTable.css"
import React, {useState} from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import RootState from "../../../redux/states/root.state";
import {canSelectUser} from "../Interfaces/UserRoleManagement";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { DataTableFoot, SortButton } from "./DataTableParts";
import { PencilIcon, TrashIcon } from "../Icons/Icons";

type Order = 'asc' | 'desc';
// Columns the API can't sort by
const unsortableHeaders = ['checked', 'edit', 'delete', 'city', 'region'];
const getHeaders = (canEdit: boolean, canDelete: boolean) => {
  const baseHeaders = [
    {id:'checked', label:'', align:'center'},
    {id:'name', label:'Name', align: null},
    {id:'username', label:'Username', align:null},
    {id:'email', label:'Email', align:null},
    {id:'city', label:'City', align:null},
    {id:'region', label:'Region', align:null},
    {id:'spas_count', label:'Spas', align:'center'},
  ];

  if (canEdit) {
    baseHeaders.push({id:'edit', label:'Edit', align:'center'});
  }

  if (canDelete) {
    baseHeaders.push({id:'delete', label:'Delete', align:'center'});
  }

  return baseHeaders;
};

// Row actions pinned to the right edge (px, fits each heading)
const actionWidths: Record<string, number> = {
  edit: 60,
  delete: 68,
};

interface UsersTableProps {
    setEditOpen: any,
    setDeleteOpen: any,
    setUser: any,
    isChecked: any,
    setChecked: any,
    isCheckAll: any,
    setCheckAll:any,
    // Page controls (filters toggle, bulk actions) shown in the table's toolbar
    toolbar?: React.ReactNode,
}

const UsersTable: React.FC<UsersTableProps> = ({setEditOpen, setDeleteOpen, setUser, isChecked, setChecked, isCheckAll, setCheckAll, toolbar}) => {
  const navigate = useNavigate();
  // Data
  const {data:users,loading:isLoading, meta} = useSelector((state: RootState) => state.users);
  const currentUser = useSelector((state: RootState) => state.user.data);
  const {canAccess:canEditUser} = usePermissionCheck(["update_user"]);
  const {canAccess:canDeleteUser} = usePermissionCheck(["delete_any_user"]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Table Sort (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.last_page ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
    setChecked([]);
    setCheckAll(false);
  }

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  // Handle Selecting Users
  const checkAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Filter users that can be selected based on role hierarchy
      const selectableUsers = users.filter((user:any) =>
          canSelectUser(currentUser?.roles[0]?.name,user?.roles[0]?.name,currentUser?.id, user?.id)
      );
      setChecked(selectableUsers);
      setCheckAll(true);
    } else {
      setChecked([]);
      setCheckAll(false);
    }
  };


  const handleCheck = (user: any) => {
    if (!canSelectUser(currentUser?.roles[0]?.name,user?.roles[0]?.name,currentUser?.id, user?.id)) {
      return; // Prevent selection if not allowed
    }

    setCheckAll(false);
    let newArr = [...isChecked ?? []];
    let found = isChecked?.find((checked_user: any) => user?.id === checked_user?.id) !== undefined;
    if (found) {
      newArr = newArr.filter((checked_user: any) => checked_user?.id !== user?.id);
    } else {
      newArr.push(user);
    }
    setChecked(newArr);
  };


  // Handle Open Modals
  const handleModal = (modal:`edit`|`delete`, user:any) => {
    setUser(user);
    modal===`edit`?setEditOpen(true):setDeleteOpen(true);
  }

  const isCheckDisabled = (user:any) => {
      return !canSelectUser(currentUser?.roles[0]?.name,user?.roles[0]?.name,currentUser?.id, user?.id);
  }

  const isUserChecked = (user: any) => isChecked?.find((checked_user:any)=>user?.id===checked_user?.id)!==undefined;

  // ---- Columns ----
  const headers = getHeaders(canEditUser, canDeleteUser);
  const pinned = headers.map((header)=>header.id).filter((id)=>id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    header.id === 'checked' ? 'dt-check-cell' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  const renderCell = (id: string, user: any) => {
    switch (id) {
      case 'checked':
        return (
          <input
            type="checkbox"
            id={`${user?.id}-checked`}
            className="dt-check"
            checked={isUserChecked(user)}
            onChange={()=>handleCheck(user)}
            disabled={isCheckDisabled(user)}
            aria-label={`Select ${user?.name || user?.username}`}
          />
        );

      case 'name':
        return (
          <a
            href={`/user/${user?.id}`}
            className="dt-name"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/user/${user?.id}`);
            }}
          >
            {user?.name && user.name.length>0? user.name : user.username}
          </a>
        );

      case 'username':
        return user?.username;

      case 'email':
        return user?.email;

      case 'city':
        return user?.city || <span className="dt-na">—</span>;

      case 'region':
        return user?.region || <span className="dt-na">—</span>;

      case 'spas_count':
        return <span className={`dt-num ${user?.spas_count ? '' : 'dt-na'}`}>{user?.spas_count ?? 0}</span>;

      case 'edit':
        return (
          <Tooltip title="Edit user" arrow>
            <button type="button" id="edit-user" className="dt-act" onClick={()=>handleModal(`edit`,user)} aria-label="Edit user">
              <PencilIcon/>
            </button>
          </Tooltip>
        );

      case 'delete':
        return (
          <Tooltip title="Delete user" arrow>
            <button type="button" id="delete-user" className="dt-act danger" onClick={()=>handleModal(`delete`,user)} aria-label="Delete user">
              <TrashIcon/>
            </button>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  const someChecked = !isCheckAll && (isChecked?.length ?? 0) > 0;

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading users"/></div>
        ) : users.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {header.id === 'checked' ? (
                      <input
                        type="checkbox"
                        id="all-checked"
                        className="dt-check"
                        checked={isCheckAll}
                        ref={(el)=>{ if (el) el.indeterminate = someChecked; }}
                        onChange={(e)=>checkAll(e)}
                        aria-label="Select all users on this page"
                      />
                    ) : !unsortableHeaders.includes(header.id) ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {users.map((user:any, i:number)=>(
                <tr key={user?.id ?? i} className={isUserChecked(user) ? 'selected' : undefined}>
                  {headers.map((header)=>(
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, user)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            {/* Not a <p>: Tables.css positions every `.list p` absolutely */}
            <div className="dt-empty-title">No Users Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={users?.length ?? 0}
        total={total}
        noun={total === 1 ? 'User' : 'Users'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};

export default UsersTable;
